/**
 * DexBuilder - Competitive Pokemon Team Builder
 * Main component using modular architecture
 * Features: team building, export as image, responsive design
 */
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { usePokemon } from '../hooks/usePokemon';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PokemonCard from '../components/dexbuilder/PokemonCard';
import EditModal from '../components/dexbuilder/EditModal';
import ExportButton from '../components/dexbuilder/ExportButton';
import { formatPokemonName } from '../data/pokemonTranslations';
import { TEAM_TEMPLATES } from '../data/teamTemplates';
import { parseShowdownFormat } from '../utils/showdownParser';
import styles from './DexBuilder.module.css';

const MAX_TEAM_SIZE = 6;
const MAX_MOVES = 4;
const MAX_SP_TOTAL = 66;
const MAX_SP_SINGLE = 32;

const COMMON_ITEMS = [
  'life-orb', 'leftovers', 'choice-scarf', 'choice-band', 'choice-specs',
  'focus-sash', 'assault-vest', 'rocky-helmet', 'heavy-duty-boots', 'eviolite',
  'black-sludge', 'air-balloon', 'weakness-policy', 'expert-belt', 'light-clay',
  'mental-herb', 'power-herb', 'white-herb', 'focus-band', 'muscle-band',
  'scope-lens', 'wide-lens', 'bright-powder', 'kings-rock', 'metronome',
  'eject-button', 'eject-pack', 'red-card', 'shed-shell', 'covert-cloak',
  'clear-amulet', 'booster-energy', 'loaded-dice', 'punching-glove',
  'safety-goggles', 'protective-pads', 'utility-umbrella', 'mirror-herb',
  'shell-bell', 'lum-berry', 'sitrus-berry', 'iapapa-berry', 'figy-berry',
  'damp-rock', 'heat-rock', 'smooth-rock', 'icy-rock', 'terrain-extender',
  'light-ball', 'thick-club', 'soul-dew', 'deep-sea-tooth', 'deep-sea-scale',
  'lucky-punch', 'leek', 'flame-orb', 'toxic-orb', 'iron-ball', 'razor-fang',
  'razor-claw', 'quick-claw', 'wise-glasses', 'zoom-lens', 'grip-claw',
  'absorb-bulb', 'cell-battery', 'luminous-moss', 'snowball', 'throat-spray',
  'electric-seed', 'grassy-seed', 'misty-seed', 'psychic-seed', 'berry-juice',
  'big-root', 'binding-band', 'blunder-policy', 'room-service', 'adamant-orb',
  'lustrous-orb', 'griseous-orb', 'metal-powder', 'quick-powder',
  'chesto-berry', 'pecha-berry', 'rawst-berry', 'aspear-berry', 'leppa-berry',
  'oran-berry', 'persim-berry', 'wiki-berry', 'mago-berry', 'aguav-berry',
  'liechi-berry', 'ganlon-berry', 'salac-berry', 'petaya-berry', 'apicot-berry',
  'lansat-berry', 'starf-berry', 'enigma-berry', 'micle-berry', 'custap-berry',
  'jaboca-berry', 'rowap-berry', 'occa-berry', 'passho-berry', 'wacan-berry',
  'rindo-berry', 'yache-berry', 'chople-berry', 'kebia-berry', 'shuca-berry',
  'coba-berry', 'payapa-berry', 'tanga-berry', 'charti-berry', 'kasib-berry',
  'haban-berry', 'colbur-berry', 'babiri-berry', 'roseli-berry',
  'charcoal', 'mystic-water', 'magnet', 'miracle-seed', 'never-melt-ice',
  'black-belt', 'poison-barb', 'soft-sand', 'sharp-beak', 'twisted-spoon',
  'silver-powder', 'hard-stone', 'spell-tag', 'dragon-fang', 'black-glasses',
  'metal-coat', 'silk-scarf', 'pixie-plate', 'draco-plate', 'earth-plate',
  'fist-plate', 'flame-plate', 'icicle-plate', 'insect-plate', 'iron-plate',
  'meadow-plate', 'mind-plate', 'sky-plate', 'splash-plate', 'spooky-plate',
  'stone-plate', 'toxic-plate', 'zap-plate'
];

const NATURE_KEYS = [
  'hardy', 'lonely', 'brave', 'adamant', 'naughty', 'bold', 'docile', 'relaxed',
  'impish', 'lax', 'timid', 'hasty', 'serious', 'jolly', 'naive', 'modest',
  'mild', 'quiet', 'bashful', 'rash', 'calm', 'gentle', 'sassy', 'careful', 'quirky'
];

// Helper: Create empty team slot
function createEmptySlot() {
  return {
    id: null, name: '', nickname: '', level: 50, gender: 'random',
    nature: 'hardy', ability: '', item: { name: '', sprite: '' }, teraType: '',
    moves: ['', '', '', ''],
    evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
    ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
    shiny: false, data: null
  };
}

// Helper: Generate logical random data for a Pokemon
// For random teams: NO tera type, ALWAYS has item
function hydratePokemon(data, overrides = {}, isRandom = false) {
  if (!data) return createEmptySlot();
  
  // Pick random ability (safely)
  const abilities = data.abilities?.map(a => a.ability.name) || [];
  const ability = abilities.length > 0 ? abilities[Math.floor(Math.random() * abilities.length)] : '';
  
  // Pick random item (safely) - ALWAYS has item for random teams
  const itemSlug = COMMON_ITEMS[Math.floor(Math.random() * COMMON_ITEMS.length)];
  
  // Pick random moves (safely)
  const possibleMoves = data.moves?.map(m => m.move.name) || [];
  let moves = ['', '', '', ''];
  if (possibleMoves.length > 0) {
    const shuffled = [...possibleMoves].sort(() => 0.5 - Math.random());
    moves = [shuffled[0] || '', shuffled[1] || '', shuffled[2] || '', shuffled[3] || ''];
  }
  
  // Pick random nature (safely)
  const nature = NATURE_KEYS[Math.floor(Math.random() * NATURE_KEYS.length)];
  
  // Logical random EVs (now SP: max 2 stats of 32 each, 2 leftover)
  const evs = { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };
  const stats = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
  const main1 = stats[Math.floor(Math.random() * stats.length)];
  const main2 = stats.filter(s => s !== main1)[Math.floor(Math.random() * 5)];
  evs[main1] = 32;
  evs[main2] = 32;
  evs[stats.find(s => s !== main1 && s !== main2)] = 2;

  const types = data.types?.map(t => t.type.name) || [];

  return {
    ...createEmptySlot(),
    id: data.id,
    name: data.name,
    nature: overrides.nature || nature,
    ability: overrides.ability || ability,
    item: overrides.item || {
      name: itemSlug,
      sprite: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${itemSlug}.png`
    },
    moves: overrides.moves || moves,
    evs: overrides.evs || evs,
    level: overrides.level || 50,
    // Random teams should NOT have tera type
    teraType: isRandom ? '' : (overrides.teraType || (types[Math.floor(Math.random() * types.length)] || 'normal')),
    shiny: overrides.shiny !== undefined ? overrides.shiny : Math.random() > 0.95,
    data,
    ...overrides
  };
}

export default function DexBuilder() {
  const { language } = useApp();
  const { getPokemon, getPokemonList } = usePokemon();

  // Load from localStorage on init
  const [teams, setTeams] = useState(() => {
    try {
      const saved = localStorage.getItem('dexbuilder-teams');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (error) {
      console.error('Failed to load teams from localStorage:', error);
    }
    return [{ name: language === 'es' ? 'Equipo 1' : 'Team 1', slots: Array(MAX_TEAM_SIZE).fill(null).map(() => createEmptySlot()), notes: '' }];
  });
  
  const [activeTeamIndex, setActiveTeamIndex] = useState(0);
  
  // Safe activeTeam getter
  const activeTeam = (teams && teams[activeTeamIndex]) || (teams && teams[0]) || { name: 'Team', slots: Array(MAX_TEAM_SIZE || 6).fill(null).map(() => createEmptySlot()), notes: '' };

  // Persist to localStorage with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        localStorage.setItem('dexbuilder-teams', JSON.stringify(teams));
      } catch (error) {
        console.error('Failed to save teams to localStorage:', error);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [teams]);

  // UI state
  const [showPokemonSelector, setShowPokemonSelector] = useState(false);
  const [showPokemonEditor, setShowPokemonEditor] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedPokemonData, setSelectedPokemonData] = useState(null);
  const [pokemonList, setPokemonList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pokepastUrl, setPokepastUrl] = useState('');
  const [pokepastLoading, setPokepastLoading] = useState(false);

  // Handle Showdown import
  const handleImportFromShowdown = async () => {
    if (!importText.trim()) return;

    try {
      toast.loading(language === 'es' ? 'Importando...' : 'Importing...');
      const parsed = parseShowdownFormat(importText);

      if (parsed.length === 0) {
        toast.dismiss();
        toast.error(language === 'es' ? 'No se encontraron Pokémon' : 'No Pokémon found');
        return;
      }

      const newSlots = Array(MAX_TEAM_SIZE).fill(null).map(() => createEmptySlot());

      for (let i = 0; i < parsed.length && i < MAX_TEAM_SIZE; i++) {
        const p = parsed[i];
        try {
          const data = await getPokemon(p.name);
          if (data) {
            newSlots[i] = {
              ...createEmptySlot(),
              id: data.id,
              name: data.name,
              nickname: p.nickname,
              level: p.level,
              gender: p.gender,
              nature: p.nature,
              ability: p.ability,
              item: p.item,
              teraType: p.teraType,
              moves: p.moves,
              evs: p.evs,
              ivs: p.ivs,
              shiny: p.shiny,
              data
            };
          }
        } catch (err) {
          console.error(`Failed to import ${p.name}:`, err);
        }
      }

      updateActiveTeam({ ...activeTeam, slots: newSlots });
      setShowImportModal(false);
      setImportText('');
      toast.dismiss();
      toast.success(language === 'es' ? `${parsed.length} Pokémon importados` : `${parsed.length} Pokémon imported`);
    } catch (error) {
      console.error('Import error:', error);
      toast.dismiss();
      toast.error(language === 'es' ? 'Error al importar' : 'Import failed');
    }
  };

  // Load Pokemon list on mount
  useEffect(() => {
    const loadPokemonList = async () => {
      try {
        const list = await getPokemonList();
        if (Array.isArray(list)) {
          setPokemonList(list);
        }
      } catch (error) {
        console.error('Failed to load Pokemon list:', error);
        toast.error(language === 'es' ? 'Error cargando lista' : 'Failed to load list');
      }
    };

    loadPokemonList();
  }, [getPokemonList, language]);

  // Active team helper - Always use functional updates for reliable state
  const updateActiveTeam = (updater) => {
    setTeams(prev => {
      const newTeams = [...prev];
      const currentTeam = newTeams[activeTeamIndex];
      if (!currentTeam) return prev;
      
      const updatedData = typeof updater === 'function' ? updater(currentTeam) : updater;
      newTeams[activeTeamIndex] = { ...currentTeam, ...updatedData };
      return newTeams;
    });
  };

  // Handle Pokemon selection from modal
  const handlePokemonSelect = async (pokemonId) => {
    try {
      const data = await getPokemon(pokemonId);
      if (!data) {
        toast.error(language === 'es' ? 'No se encontró' : 'Not found');
        return;
      }

      // If we don't have an editing index, find the first empty slot
      // We must do this BEFORE opening the editor
      let indexToUse = editingIndex;
      if (indexToUse === null) {
        const firstEmpty = activeTeam.slots.findIndex(s => !s.data);
        indexToUse = firstEmpty !== -1 ? firstEmpty : 0;
        setEditingIndex(indexToUse);
      }

      setSelectedPokemonData(data);
      setShowPokemonSelector(false);
      setShowPokemonEditor(true);
    } catch (error) {
      console.error('Error fetching Pokemon:', error);
      toast.error(language === 'es' ? 'Error cargando Pokémon' : 'Failed to load Pokémon');
    }
  };

  // Handle save from editor
  const handleSaveSlot = (updatedSlot) => {
    if (editingIndex === null) {
      console.error('No editing index found');
      return;
    }
    
    updateActiveTeam(team => {
      const newSlots = [...team.slots];
      newSlots[editingIndex] = updatedSlot;
      return { slots: newSlots };
    });
    
    setShowPokemonEditor(false);
    setEditingIndex(null);
    setSelectedPokemonData(null);
    toast.success(language === 'es' ? 'Guardado' : 'Saved');
  };

  // Handle edit
  const handleEdit = (index) => {
    setEditingIndex(index);
    const slot = activeTeam.slots[index];

    if (slot.data) {
      setSelectedPokemonData(slot.data);
      setShowPokemonEditor(true);
      setShowPokemonSelector(false);
    } else {
      setShowPokemonSelector(true);
      setShowPokemonEditor(false);
    }
  };

  // Handle remove
  const handleRemove = (index) => {
    updateActiveTeam(team => {
      const newSlots = [...team.slots];
      newSlots[index] = createEmptySlot();
      return { ...team, slots: newSlots };
    });
    toast.success(language === 'es' ? 'Eliminado' : 'Removed');
  };

  // Handle random team generation
  const generateRandomTeam = async () => {
    const list = pokemonList || [];
    if (list.length === 0) {
      toast.error(language === 'es' ? 'Lista de Pokémon no disponible' : 'Pokémon list not available');
      return;
    }
    
    try {
      toast.loading(language === 'es' ? 'Generando equipo aleatorio...' : 'Generating random team...');
      
      const shuffled = [...list].filter(p => p && p.id).sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, MAX_TEAM_SIZE);
      
      const newSlots = await Promise.all(selected.map(async (p) => {
        try {
          const data = await getPokemon(p.id);
          // Pass isRandom=true to not generate tera type
          return hydratePokemon(data, {}, true);
        } catch (err) {
          console.error(`Error hydrating random pokemon:`, err);
          return createEmptySlot();
        }
      }));
      
      updateActiveTeam({ slots: newSlots });
      toast.dismiss();
      toast.success(language === 'es' ? '¡Equipo aleatorio generado!' : 'Random team generated!');
    } catch (err) {
      console.error('Random generation failed:', err);
      toast.dismiss();
      toast.error(language === 'es' ? 'Error al generar equipo' : 'Generation failed');
    }
  };

  // Handle template application
  const applyTemplate = async (templateKey) => {
    if (templateKey === 'random') {
      await generateRandomTeam();
      return;
    }

    const template = TEAM_TEMPLATES[templateKey];
    if (!template) return;

    try {
      toast.loading(language === 'es' ? 'Aplicando plantilla...' : 'Applying template...');
      const hydratedTeam = await Promise.all(template.pokemon.map(async (p, i) => {
        try {
          const data = await getPokemon(p.id);
          if (!data) return createEmptySlot();

          // Use template data directly - templates now contain complete info
          return {
            id: data.id,
            name: data.name,
            nickname: p.nickname || '',
            level: p.level || 50,
            gender: p.gender || 'random',
            nature: p.nature || 'hardy',
            ability: p.ability || (data.abilities?.[0]?.ability.name || ''),
            item: p.item ? {
              name: p.item,
              sprite: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${p.item}.png`
            } : { name: '', sprite: '' },
            teraType: p.teraType || (data.types?.[0]?.type.name || 'normal'),
            moves: p.moves || ['', '', '', ''],
            evs: p.evs || { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
            ivs: p.ivs || { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
            shiny: p.shiny || false,
            data
          };
        } catch (err) {
          console.error(`Failed to load template pokemon ${p.name}:`, err);
          return createEmptySlot();
        }
      }));

      updateActiveTeam(team => {
        const newSlots = [...team.slots];
        hydratedTeam.forEach((slot, i) => {
          if (i < MAX_TEAM_SIZE) newSlots[i] = slot;
        });
        return { ...team, slots: newSlots };
      });

      toast.dismiss();
      toast.success(language === 'es' ? 'Plantilla aplicada' : 'Template applied');
    } catch (error) {
      console.error('Error applying template:', error);
      toast.dismiss();
      toast.error(language === 'es' ? 'Error al aplicar plantilla' : 'Error applying template');
    }
  };

  // Tab management
  const addTab = () => {
    const newTeam = {
      name: language === 'es' ? `Equipo ${teams.length + 1}` : `Team ${teams.length + 1}`,
      slots: Array(MAX_TEAM_SIZE).fill(null).map(() => createEmptySlot()),
      notes: ''
    };
    setTeams([...teams, newTeam]);
    setActiveTeamIndex(teams.length);
  };

  const removeTab = (index) => {
    if (teams.length <= 1) {
      toast.error(language === 'es' ? 'Debes tener al menos un equipo' : 'You must have at least one team');
      return;
    }
    if (window.confirm(language === 'es' ? '¿Eliminar este equipo?' : 'Delete this team?')) {
      const newTeams = teams.filter((_, i) => i !== index);
      setTeams(newTeams);
      setActiveTeamIndex(Math.max(0, activeTeamIndex >= index ? activeTeamIndex - 1 : activeTeamIndex));
    }
  };

  const clearTeam = () => {
    const emptySlots = Array(MAX_TEAM_SIZE).fill(null).map(() => createEmptySlot());
    updateActiveTeam({ slots: emptySlots });
    toast.success(language === 'es' ? 'Equipo vaciado' : 'Team cleared');
  };

  // Upload current paste to pokepast.es and get a shareable URL
  const uploadToPokepast = async () => {
    const paste = generatePaste(activeTeam.slots, language);
    if (!paste.trim()) {
      toast.error(language === 'es' ? 'El equipo está vacío' : 'Team is empty');
      return;
    }
    setPokepastLoading(true);
    setPokepastUrl('');
    try {
      const res = await fetch('/api/pokepast/new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paste,
          author: 'DexRiddle',
          title: activeTeam.name || 'My Team',
          notes: activeTeam.notes || '',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const url = data.url || data.link || '';
      if (!url) throw new Error('No URL returned');
      setPokepastUrl(url);
      navigator.clipboard.writeText(url).catch(() => {});
      toast.success(language === 'es' ? '¡Enlace copiado!' : 'Link copied!');
    } catch (err) {
      console.error('Pokepast error:', err);
      toast.error(language === 'es' ? 'Error al subir a Pokepast.es' : 'Failed to upload to Pokepast.es');
    } finally {
      setPokepastLoading(false);
    }
  };

  const teamSize = activeTeam.slots.filter(s => s.data).length;
  const filteredPokemon = pokemonList.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.id.toString().includes(searchQuery)
  );

  return (
    <>
      <Helmet>
        <title>{language === 'es' ? 'Constructor de Equipo - DexRiddle' : 'Team Builder - DexRiddle'}</title>
      </Helmet>

      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          {/* Team Tabs */}
          <div className={styles.teamTabs}>
            {teams.map((team, idx) => (
              <button
                key={idx}
                className={`${styles.teamTab} ${activeTeamIndex === idx ? styles.teamTabActive : ''}`}
                onClick={() => setActiveTeamIndex(idx)}
              >
                {team.name}
                {teams.length > 1 && (
                  <span
                    className={styles.teamTabClose}
                    onClick={(e) => { e.stopPropagation(); removeTab(idx); }}
                  >
                    ×
                  </span>
                )}
              </button>
            ))}
            <button className={styles.addTeamBtn} onClick={addTab}>
              <Plus size={14} />
              {language === 'es' ? 'Nuevo Equipo' : 'New Team'}
            </button>
          </div>

          {/* Settings Card: Team Name + Notes + Toolbar */}
          <div className={styles.settingsCard}>
            <div className={styles.settingsGrid}>
              <div>
                <label className={styles.settingsLabel}>
                  {language === 'es' ? 'Nombre del equipo' : 'Team Name'}
                </label>
                <input
                  type="text"
                  className={styles.settingsInput}
                  value={activeTeam.name}
                  onChange={(e) => updateActiveTeam({ ...activeTeam, name: e.target.value })}
                  placeholder={language === 'es' ? 'Mi equipo competitivo' : 'My Competitive Team'}
                />
              </div>
              <div>
                <label className={styles.settingsLabel}>
                  {language === 'es' ? 'Notas de estrategia' : 'Team Strategy Notes'}
                </label>
                <textarea
                  className={styles.settingsTextarea}
                  value={activeTeam.notes}
                  onChange={(e) => updateActiveTeam({ ...activeTeam, notes: e.target.value })}
                  placeholder={language === 'es' ? 'Añadir notas de estrategia...' : 'Add strategy notes here...'}
                />
              </div>
            </div>

            {/* Toolbar */}
            <div className={styles.toolbar}>
              <div className={styles.toolbarLeft}>
                <button className={styles.toolBtn} onClick={() => setShowImportModal(true)} data-testid="import-showdown-btn">
                  <svg className={styles.toolBtnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  {language === 'es' ? 'Importar Showdown' : 'Import Showdown'}
                </button>
                <button className={styles.toolBtn} onClick={() => setShowPasteModal(true)} data-testid="dexpaste-btn">
                  <svg className={styles.toolBtnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>
                  DexPaste
                </button>
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <select
                    className={styles.toolBtn}
                    value=""
                    onChange={(e) => applyTemplate(e.target.value)}
                    style={{ appearance: 'none', paddingRight: '1.75rem', cursor: 'pointer' }}
                    data-testid="templates-btn"
                  >
                    <option value="" disabled>
                      {language === 'es' ? '📋 Plantillas' : '📋 Templates'}
                    </option>
                    <option value="random">{language === 'es' ? '🎲 Todo Aleatorio' : '🎲 Everything Random'}</option>
                    {Object.entries(TEAM_TEMPLATES).map(([key, template]) => (
                      <option key={key} value={key}>
                        {language === 'es' ? template.name.es : template.name.en}
                      </option>
                    ))}
                  </select>
                  <svg style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', width: '12px', height: '12px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                <button className={styles.clearBtn} onClick={clearTeam} data-testid="clear-team-btn">
                  <svg className={styles.toolBtnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  {language === 'es' ? 'Vaciar Equipo' : 'Clear Team'}
                </button>
              </div>

              <ExportButton
                team={activeTeam.slots}
                teamName={activeTeam.name}
                teamNotes={activeTeam.notes}
                disabled={teamSize === 0}
                renderCustom={(onClick, disabled) => (
                  <button className={styles.exportBtn} onClick={onClick} disabled={disabled} data-testid="export-btn">
                    <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {language === 'es' ? 'Exportar como Imagen' : 'Export as Image'}
                  </button>
                )}
              />
            </div>
          </div>

          {/* Team Grid — 2 columns */}
          <div className={styles.teamGrid}>
            {activeTeam.slots.map((slot, index) => (
              <motion.div
                key={`${activeTeamIndex}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <PokemonCard
                  slot={slot}
                  index={index}
                  onEdit={handleEdit}
                  onRemove={handleRemove}
                />
              </motion.div>
            ))}
          </div>

          {/* Pokemon Selector Modal */}
          <AnimatePresence>
            {showPokemonSelector && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={styles.modal}
                onClick={() => setShowPokemonSelector(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className={styles.modalContent}
                  onClick={e => e.stopPropagation()}
                  style={{ maxWidth: '800px', maxHeight: '80vh' }}
                >
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
                    {language === 'es' ? 'Seleccionar Pokémon' : 'Select Pokémon'}
                  </h2>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={language === 'es' ? 'Buscar por nombre o #...' : 'Search by name or #...'}
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.75rem',
                        border: '1px solid #e9d5ff',
                        fontSize: '1rem',
                        marginBottom: '1rem',
                        outline: 'none'
                      }}
                    />

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                      gap: '0.75rem',
                      maxHeight: '50vh',
                      overflowY: 'auto'
                    }}>
                      {filteredPokemon.map(pokemon => (
                        <button
                          key={pokemon.id}
                          onClick={() => handlePokemonSelect(pokemon.id)}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.75rem',
                            borderRadius: '0.75rem',
                            border: '1px solid #e9d5ff',
                            background: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            fontSize: '0.75rem'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124,58,237,0.12)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e9d5ff'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                          <img
                            src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemon.id}.png`}
                            alt={pokemon.name}
                            style={{ width: '48px', height: '48px' }}
                          />
                          <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{pokemon.name}</span>
                          <span style={{ color: '#9ca3af' }}>#{pokemon.id}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => setShowPokemonSelector(false)}
                    style={{ width: '100%', borderRadius: '0.75rem' }}
                  >
                    {language === 'es' ? 'Cerrar' : 'Close'}
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Import Modal */}
          <AnimatePresence>
            {showImportModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={styles.modal}
                onClick={() => setShowImportModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className={styles.modalContent}
                  onClick={e => e.stopPropagation()}
                  style={{ maxWidth: '600px' }}
                >
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                    {language === 'es' ? 'Importar formato Showdown' : 'Import Showdown Format'}
                  </h2>
                  <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '1.5rem' }}>
                    {language === 'es'
                      ? 'Pega el texto de tu equipo exportado de Pokémon Showdown aquí.'
                      : 'Paste your exported Pokémon Showdown team text here.'}
                  </p>

                  <textarea
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder="Pikachu @ Light Ball..."
                    style={{
                      width: '100%',
                      minHeight: '200px',
                      padding: '1rem',
                      borderRadius: '0.75rem',
                      border: '1px solid #e9d5ff',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      marginBottom: '1.5rem',
                      resize: 'vertical',
                      outline: 'none'
                    }}
                    autoFocus
                  />

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <Button variant="outline" onClick={() => setShowImportModal(false)} style={{ flex: 1, borderRadius: '0.75rem' }}>
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                    <Button
                      onClick={handleImportFromShowdown}
                      style={{ flex: 1, backgroundColor: '#7c3aed', color: 'white', borderRadius: '0.75rem' }}
                    >
                      {language === 'es' ? 'Importar' : 'Import'}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Edit Modal */}
          <AnimatePresence>
            {showPokemonEditor && (
              <EditModal
                isOpen={showPokemonEditor}
                onClose={() => {
                  setShowPokemonEditor(false);
                  setSelectedPokemonData(null);
                  setEditingIndex(null);
                }}
                slot={activeTeam.slots[editingIndex]}
                onSave={handleSaveSlot}
                pokemonData={selectedPokemonData}
                language={language}
              />
            )}

            {/* Paste Export Modal */}
            {showPasteModal && (
              <div className={styles.modal} onClick={() => { setShowPasteModal(false); setPokepastUrl(''); }}>
                <div
                  className={styles.modalContent}
                  onClick={e => e.stopPropagation()}
                  style={{ maxWidth: '600px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>DexPaste (Showdown Format)</h3>
                    <button
                      onClick={() => { setShowPasteModal(false); setPokepastUrl(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                    >
                      <Plus style={{ transform: 'rotate(45deg)' }} />
                    </button>
                  </div>
                  <textarea
                    readOnly
                    value={generatePaste(activeTeam.slots, language)}
                    style={{
                      width: '100%',
                      height: '300px',
                      padding: '1rem',
                      background: '#faf5ff',
                      border: '1px solid #e9d5ff',
                      borderRadius: '0.75rem',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      marginBottom: '1rem',
                      resize: 'none',
                      color: 'inherit'
                    }}
                  />

                  {/* Pokepast.es URL result */}
                  {pokepastUrl && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.625rem 0.875rem',
                      background: '#f0fdf4',
                      border: '1px solid #86efac',
                      borderRadius: '0.5rem',
                      marginBottom: '1rem',
                    }}>
                      <a
                        href={pokepastUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ flex: 1, fontSize: '0.875rem', color: '#15803d', fontWeight: 600, wordBreak: 'break-all' }}
                      >
                        {pokepastUrl}
                      </a>
                      <button
                        onClick={() => { navigator.clipboard.writeText(pokepastUrl); toast.success(language === 'es' ? 'Copiado' : 'Copied'); }}
                        title={language === 'es' ? 'Copiar enlace' : 'Copy link'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: '#15803d', flexShrink: 0 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      </button>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                    <Button onClick={() => {
                      navigator.clipboard.writeText(generatePaste(activeTeam.slots, language));
                      toast.success(language === 'es' ? 'Copiado al portapapeles' : 'Copied to clipboard');
                    }} style={{ borderRadius: '0.75rem' }}>
                      {language === 'es' ? 'Copiar texto' : 'Copy text'}
                    </Button>
                    <Button
                      onClick={uploadToPokepast}
                      disabled={pokepastLoading}
                      style={{ borderRadius: '0.75rem', background: 'linear-gradient(to right, #7c3aed, #e11d48)', color: 'white', opacity: pokepastLoading ? 0.7 : 1 }}
                    >
                      {pokepastLoading
                        ? (language === 'es' ? 'Subiendo...' : 'Uploading...')
                        : 'Pokepast.es'}
                    </Button>
                    <Button onClick={() => { setShowPasteModal(false); setPokepastUrl(''); }} variant="outline" style={{ borderRadius: '0.75rem' }}>
                      {language === 'es' ? 'Cerrar' : 'Close'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}

// Helper for generating paste
function generatePaste(slots, language) {
  if (!slots || !Array.isArray(slots)) return '';
  
  return slots.filter(s => s?.data).map(slot => {
    const p = slot.data;
    if (!p) return '';
    
    const name = slot.nickname 
      ? `${slot.nickname} (${p.name.charAt(0).toUpperCase() + p.name.slice(1).replace(/-/g, ' ')})` 
      : (p.name.charAt(0).toUpperCase() + p.name.slice(1).replace(/-/g, ' '));
    const itemName = typeof slot.item === 'object' ? slot.item?.name : slot.item;
    
    // Format helpers for Showdown
    const fmt = (s) => {
      if (!s) return '';
      return s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };
    
    let text = `${name}${itemName ? ` @ ${fmt(itemName)}` : ''}\n`;
    if (slot.ability) text += `Ability: ${fmt(slot.ability)}\n`;
    if (slot.level && slot.level !== 50) text += `Level: ${slot.level}\n`;
    if (slot.shiny) text += `Shiny: Yes\n`;
    if (slot.teraType) text += `Tera Type: ${fmt(slot.teraType)}\n`;
    
    const evs = Object.entries(slot.evs || {}).filter(([_, v]) => v > 0);
    if (evs.length > 0) {
      const evMap = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };
      text += `EVs: ${evs.map(([k, v]) => `${v} ${evMap[k]}`).join(' / ')}\n`;
    }
    
    if (slot.nature) text += `${slot.nature.charAt(0).toUpperCase() + slot.nature.slice(1)} Nature\n`;
    
    const ivs = Object.entries(slot.ivs || {}).filter(([_, v]) => v < 31);
    if (ivs.length > 0) {
      const ivMap = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };
      text += `IVs: ${ivs.map(([k, v]) => `${v} ${ivMap[k]}`).join(' / ')}\n`;
    }
    
    (slot.moves || []).filter(m => m).forEach(move => {
      text += `- ${fmt(move)}\n`;
    });
    
    return text;
  }).filter(t => t).join('\n');
}
